/**
 * Per-tab room session persistence, Q9 (audit 2026-06-11).
 *
 * A page reload used to mint a brand-new player (ghost rows in the roster,
 * scores lost) because the session token only ever lived in a local variable
 * inside ws.ts: the server keeps sessions alive across disconnects
 * precisely so HELLO{sessionToken, roomId} can resume, but nothing on the
 * client survived the reload to send it. RoomShell now saves the join here
 * once in-room and resumes on the next load.
 *
 * sessionStorage, NOT localStorage, on purpose: it's per-tab, so two tabs in
 * the same room stay two distinct players (the multi-tab test setup), and
 * closing the tab ends the resume window instead of leaking a stale token.
 *
 * Known caveat (review 2026-06-11): Chrome's "Duplicate Tab" COPIES
 * sessionStorage, so a duplicated tab resumes the same session, two sockets
 * attached to one player. Accepted: it's a deliberate user action, the
 * server tolerates multi-socket sessions, and the Tier 3 kick-eviction work
 * (task #13) narrows how much can break further.
 */

export type SavedRoomSession = {
  sessionToken: string;
  roomId: string;
  name: string;
  color: string;
};

const KEY = "drawlol:room-session:v1";

export function saveRoomSession(s: SavedRoomSession): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Storage can be unavailable (private mode, quota), resume is
    // best-effort; a failed save just means the old reload behavior.
  }
}

/**
 * Load the saved session, but only when it matches `roomId`, a session
 * saved for room A must never hijack a fresh join of room B. Malformed or
 * missing data returns null (fresh-join path).
 */
export function loadRoomSession(roomId: string): SavedRoomSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<SavedRoomSession> | null;
    if (
      !p ||
      typeof p.sessionToken !== "string" ||
      typeof p.roomId !== "string" ||
      typeof p.name !== "string" ||
      typeof p.color !== "string"
    ) {
      return null;
    }
    if (p.roomId !== roomId) return null;
    return {
      sessionToken: p.sessionToken,
      roomId: p.roomId,
      name: p.name,
      color: p.color,
    };
  } catch {
    return null;
  }
}

export function clearRoomSession(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore: same best-effort stance as saveRoomSession
  }
}
